import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getTopVouchers, applyVoucher, getAppliedVouchers } from "../../services/vouchers";
import { getPromoCodes, applyPromoCode, getAppliedPromoCodes } from "../../services/promoCodes";
import { toast } from "react-toastify";
import "../../styles/VouchersPage.css";

// ── Icons ──────────────────────────────────────────────
const TagIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
    <line x1="7" y1="7" x2="7.01" y2="7"/>
  </svg>
);
const CopyIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2"/>
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
  </svg>
);
const ChevronLeft = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6"/>
  </svg>
);
const TicketIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v2z"/>
  </svg>
);
const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

// ── VouchersPage ───────────────────────────────────────
const VouchersPage = () => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const initTab   = new URLSearchParams(location.search).get("tab") === "promos" ? "promos" : "vouchers";

  const [activeTab,          setActiveTab]          = useState(initTab);
  const [vouchers,           setVouchers]           = useState([]);
  const [promoCodes,         setPromoCodes]         = useState([]);
  const [collectedIds,       setCollectedIds]       = useState(new Set());
  const [appliedIds,         setAppliedIds]         = useState(new Set());
  const [loadingVouchers,    setLoadingVouchers]    = useState(true);
  const [loadingPromos,      setLoadingPromos]      = useState(true);
  const [processingIds,      setProcessingIds]      = useState(new Set());

  const getApiMsg = (err, fb = "Something went wrong") =>
    err?.response?.data?.message || err?.response?.data?.error || err?.message || fb;

  // ── Load data ──
  useEffect(() => {
    let mounted = true;

    const loadVouchers = async () => {
      setLoadingVouchers(true);
      try {
        const { data } = await getTopVouchers();
        const raw = (data?.data || data || []).filter(x => x?.is_active === true || String(x?.is_active) === "1");
        if (mounted) setVouchers(raw.map(v => {
          const type  = String(v.type || "").toLowerCase();
          const value = Number(v.value ?? 0);
          return {
            id: v.id, code: v.code, type, value,
            minSpend: Number(v.min_purchase ?? 0),
            validFrom: v.valid_from, validUntil: v.valid_until,
            collected: !!v.collected,
            discountLabel: type === "fixed" ? `$${value.toFixed(0)} OFF` : `${value}% OFF`,
          };
        }));
      } catch { if (mounted) setVouchers([]); }
      finally { if (mounted) setLoadingVouchers(false); }
    };

    const loadCollected = async () => {
      try {
        const res = await getAppliedVouchers?.();
        const raw = res?.data?.data || res?.data || res || [];
        const ids = (Array.isArray(raw) ? raw : []).map(x => x.voucher_id ?? x.id ?? x.voucher?.id).filter(Boolean);
        if (mounted) setCollectedIds(new Set(ids));
      } catch {}
    };

    const loadPromos = async () => {
      setLoadingPromos(true);
      try {
        const res = await getPromoCodes();
        const raw = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
        if (mounted) setPromoCodes(
          raw.filter(p => String(p.is_active) === "1" || p.is_active === true)
             .map(p => ({
               id: p.id, code: p.code, type: p.type, value: Number(p.value ?? 0),
               minOrder: Number(p.min_order_amount ?? 0),
               startDate: p.start_date, endDate: p.end_date,
             }))
        );
      } catch { if (mounted) setPromoCodes([]); }
      finally { if (mounted) setLoadingPromos(false); }
    };

    const loadApplied = async () => {
      try {
        const res = await getAppliedPromoCodes?.();
        const raw = res?.data?.data || res?.data || res || [];
        const ids = (Array.isArray(raw) ? raw : []).map(x => x.promocode_id ?? x.id ?? x.promo_code_id ?? x.promo?.id).filter(Boolean);
        if (mounted) setAppliedIds(new Set(ids));
      } catch {}
    };

    loadVouchers(); loadCollected(); loadPromos(); loadApplied();
    return () => { mounted = false; };
  }, []);

  // ── Actions ──
  const handleCollect = async (id) => {
    setProcessingIds(p => new Set(p).add(id));
    setCollectedIds(p => new Set(p).add(id));
    setVouchers(p => p.map(v => v.id === id ? { ...v, collected: true } : v));
    try { await applyVoucher(id); toast.success("Voucher collected! 🎉"); }
    catch (err) {
      toast.error(getApiMsg(err, "Failed to collect voucher."));
      setCollectedIds(p => { const n = new Set(p); n.delete(id); return n; });
      setVouchers(p => p.map(v => v.id === id ? { ...v, collected: false } : v));
    }
    finally { setProcessingIds(p => { const n = new Set(p); n.delete(id); return n; }); }
  };

  const handleApply = async (id) => {
    setProcessingIds(p => new Set(p).add(id));
    setAppliedIds(p => new Set(p).add(id));
    try { await applyPromoCode(id); toast.success("Promo applied! 🎉"); }
    catch (err) {
      toast.error(getApiMsg(err, "Failed to apply promo."));
      setAppliedIds(p => { const n = new Set(p); n.delete(id); return n; });
    }
    finally { setProcessingIds(p => { const n = new Set(p); n.delete(id); return n; }); }
  };

  const handleCopy = async (code) => {
    if (!code) return;
    try { await navigator.clipboard.writeText(code); toast.success("Code copied!"); }
    catch { toast.error("Failed to copy"); }
  };

  const formatPromo = (p) =>
    String(p.type).toLowerCase() === "amount" ? `$${Number(p.value).toFixed(2)} OFF` : `${Number(p.value)}% OFF`;

  const isCollected = (v) => !!v.collected || collectedIds.has(v.id);
  const isApplied   = (p) => appliedIds.has(p.id);

  // ── Ticket card shared renderer ──
  const TicketCard = ({ item, isVoucher }) => {
    const done       = isVoucher ? isCollected(item) : isApplied(item);
    const processing = processingIds.has(item.id);
    const label      = isVoucher ? item.discountLabel : formatPromo(item);
    const subtype    = isVoucher ? (item.type === "fixed" ? "Flat Discount" : "Percentage Off") : "Promo Code";
    const code       = item.code;
    const meta1      = isVoucher
      ? `Min. spend $${Number(item.minSpend || 0).toFixed(0)}`
      : `Min. order $${Number(item.minOrder || 0).toFixed(0)}`;
    const meta2      = isVoucher
      ? (item.validFrom ? `${item.validFrom} – ${item.validUntil || "—"}` : item.validUntil ? `Expires ${item.validUntil}` : null)
      : (item.startDate ? `${item.startDate} – ${item.endDate || "—"}` : item.endDate ? `Expires ${item.endDate}` : null);

    return (
      <div className={`vp-ticket ${done ? "is-used" : ""}`}>
        {/* Left orange accent */}
        <div className="vp-ticket-accent">
          <span className="vp-ticket-value">{label}</span>
          <span className="vp-ticket-subtype">{subtype}</span>
        </div>

        {/* Tear separator */}
        <div className="vp-ticket-sep">
          <span className="vp-notch top"/><span className="vp-notch bottom"/>
        </div>

        {/* Right details */}
        <div className="vp-ticket-body">
          <div className="vp-code-row">
            <span className="vp-code">{code}</span>
            <button className="vp-copy-btn" onClick={() => handleCopy(code)} title="Copy code">
              <CopyIcon/>
            </button>
          </div>
          <div className="vp-meta-row">
            <span className="vp-meta">{meta1}</span>
            {meta2 && <span className="vp-meta vp-meta-muted">{meta2}</span>}
          </div>
          <button
            className={`vp-action-btn ${done ? "is-done" : ""}`}
            onClick={() => !done && (isVoucher ? handleCollect(item.id) : handleApply(item.id))}
            disabled={done || processing}
          >
            {done ? <><CheckIcon/> {isVoucher ? "Collected" : "Applied"}</> : processing ? "…" : isVoucher ? "Collect" : "Apply"}
          </button>
        </div>
      </div>
    );
  };

  const loading = activeTab === "vouchers" ? loadingVouchers : loadingPromos;
  const items   = activeTab === "vouchers" ? vouchers : promoCodes;
  const count   = items.length;

  return (
    <div className="vp-root">
      <div className="vp-container">

        {/* ── Page header ── */}
        <div className="vp-page-header">
          <button className="vp-back-btn" onClick={() => navigate(-1)}>
            <ChevronLeft/> Back
          </button>
          <div className="vp-page-title-row">
            <div className="vp-page-icon"><TicketIcon/></div>
            <div>
              <h1 className="vp-page-title">Deals &amp; Offers</h1>
              <p className="vp-page-sub">Collect vouchers and apply promo codes at checkout</p>
            </div>
          </div>
        </div>

        {/* ── Tabs ── */}
        <div className="vp-tabs">
          <button
            className={`vp-tab ${activeTab === "vouchers" ? "active" : ""}`}
            onClick={() => setActiveTab("vouchers")}
          >
            <TagIcon/> Vouchers
            {vouchers.length > 0 && <span className="vp-tab-count">{vouchers.length}</span>}
          </button>
          <button
            className={`vp-tab ${activeTab === "promos" ? "active" : ""}`}
            onClick={() => setActiveTab("promos")}
          >
            <TagIcon/> Promo Codes
            {promoCodes.length > 0 && <span className="vp-tab-count">{promoCodes.length}</span>}
          </button>
        </div>

        {/* ── Content ── */}
        {loading && (
          <div className="vp-loading">
            <span className="vp-spinner"/>
            Loading {activeTab === "vouchers" ? "vouchers" : "promo codes"}…
          </div>
        )}

        {!loading && count === 0 && (
          <div className="vp-empty">
            <div className="vp-empty-icon"><TicketIcon/></div>
            <p className="vp-empty-title">No {activeTab === "vouchers" ? "vouchers" : "promo codes"} available</p>
            <p className="vp-empty-sub">Check back soon for new offers and deals.</p>
          </div>
        )}

        {!loading && count > 0 && (
          <div className="vp-grid">
            {items.map(item => (
              <TicketCard key={item.id} item={item} isVoucher={activeTab === "vouchers"}/>
            ))}
          </div>
        )}

      </div>
    </div>
  );
};

export default VouchersPage;